import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Eye, EyeOff, Heart, FileJson, CheckCircle, Info, Loader2, Copy, Check } from 'lucide-react';
import CosmicBackground from '../components/CosmicBackground';
import { useParams, useLocation } from 'react-router-dom';

const RelationshipKernelViewer = () => {
  const [kernel, setKernel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [sizeInfo, setSizeInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const { userId: urlUserId } = useParams();
  const location = useLocation();
  
  // Get user ID from: URL params > location state > localStorage > default
  const userId = urlUserId || 
                 location.state?.userId || 
                 localStorage.getItem('currentKernelUserId') || 
                 "test_user_123";

  useEffect(() => {
    console.log('🚀 useEffect triggered with userId:', userId);
    if (userId) {
      // Fetch kernel first, then size info after a small delay to avoid race conditions
      const loadData = async () => {
        await fetchKernel();
        // Small delay to ensure first fetch completes
        setTimeout(() => {
          fetchSizeInfo();
        }, 500);
      };
      loadData();
    } else {
      console.error('❌ No userId available');
      setError('No user ID found. Please generate a kernel first.');
      setLoading(false);
    }
  }, []); // Only run once on mount

  const fetchKernel = async () => {
    console.log('🔍 Fetching kernel for userId:', userId);
    setLoading(true);
    setError(null);
    
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/kernel/relationship/${userId}`;
    console.log('📡 Fetching from:', url);
    
    try {
      const response = await fetch(url);
      
      console.log('📥 Response status:', response.status);
      
      // Read response body IMMEDIATELY (only once, regardless of status)
      let responseData;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }
      
      // THEN check if request was successful
      if (!response.ok) {
        const errorMsg = typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
        throw new Error(`HTTP ${response.status}: ${errorMsg}`);
      }
      
      console.log('✅ Data received, success:', responseData.success);
      
      if (responseData.success && responseData.kernel) {
        setKernel(responseData.kernel);
        console.log('✅ Kernel set successfully');
      } else {
        throw new Error('Invalid kernel data received');
      }
    } catch (err) {
      console.error('❌ Kernel fetch error:', err.message);
      setError(err.message || 'Failed to load kernel');
    } finally {
      setLoading(false);
      console.log('🏁 Loading complete');
    }
  };

  const fetchSizeInfo = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/kernel/relationship/${userId}/size`;
    
    try {
      const response = await fetch(url);
      
      // Read body IMMEDIATELY (only once)
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }
      
      // Then check status
      if (response.ok && typeof data === 'object') {
        setSizeInfo(data);
        console.log('✅ Size info loaded');
      } else {
        console.warn('Size info not available (non-critical)');
      }
    } catch (err) {
      console.error('Failed to fetch size info (non-critical):', err.message);
      // Don't show error to user, size info is optional
    }
  };

  const handleDownload = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/kernel/relationship/${userId}/download`;
    
    try {
      const response = await fetch(url);
      
      // Read body IMMEDIATELY as blob (only once)
      const blob = await response.blob();
      
      // Then check status
      if (!response.ok) {
        throw new Error(`Download failed: HTTP ${response.status}`);
      }
      
      // Create and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${userId}_relationship_kernel.json`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      }, 100);
      
      console.log('✅ Download complete');
    } catch (err) {
      console.error('Download error:', err.message);
      alert(`Failed to download kernel: ${err.message}`);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(kernel, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const toggleAllSections = (expand) => {
    const newState = {};
    Object.keys(kernel || {}).forEach(key => {
      newState[key] = expand;
    });
    setExpandedSections(newState);
  };

  const getSectionIcon = (key) => {
    const iconMap = {
      time_space: '🌍',
      core_charts: '📊',
      lagna: '🎯',
      marriage_houses: '🏠',
      planets: '🪐',
      relationship_karakas: '💫',
      key_nakshatras: '⭐',
      divisional_charts: '📈',
      jaimini: '🔮',
      special_lagnas: '✨',
      dashas: '⏰',
      kp_cusps: '📐',
      transits: '🌙',
      aspects: '🔗',
      event_time: '⏳'
    };
    return iconMap[key] || '📄';
  };

  const getSectionTitle = (key) => {
    return key.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    console.log('🔄 Rendering loading state');
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <CosmicBackground />
        <div className="text-center relative z-10">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Loading Relationship Kernel...</p>
        </div>
      </div>
    );
  }
  
  console.log('📊 Rendering main content. Kernel exists:', !!kernel);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <CosmicBackground />
        <div className="text-center bg-red-500/20 p-8 rounded-lg border border-red-500/50">
          <p className="text-red-400 text-xl mb-4">Error: {error}</p>
          <button 
            onClick={fetchKernel}
            className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white p-6">
      <CosmicBackground />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                🔱 Relationship & Marriage Kernel
              </h1>
              <p className="text-gray-400">
                Complete astrological primitives for relationship, marriage, divorce & remarriage analysis
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => toggleAllSections(true)}
                className="bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Eye className="w-5 h-5" />
                Expand All
              </button>
              <button
                onClick={() => toggleAllSections(false)}
                className="bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/50 px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <EyeOff className="w-5 h-5" />
                Collapse All
              </button>
              <button
                onClick={copyToClipboard}
                className="bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 px-4 py-2 rounded-lg flex items-center gap-2"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy JSON'}
              </button>
              <button
                onClick={handleDownload}
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 px-6 py-2 rounded-lg flex items-center gap-2 font-semibold"
              >
                <Download className="w-5 h-5" />
                Download JSON
              </button>
            </div>
          </div>

          {/* Size Comparison */}
          {sizeInfo && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30 rounded-lg p-4 mb-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-2">📊 Kernel Optimization</h3>
                  <div className="grid grid-cols-3 gap-6 text-sm">
                    <div>
                      <p className="text-gray-400">Full Kernel</p>
                      <p className="text-2xl font-bold text-red-400">{sizeInfo.full_kernel_size_kb} KB</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Relationship Kernel</p>
                      <p className="text-2xl font-bold text-green-400">{sizeInfo.relationship_kernel_size_kb} KB</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Reduction</p>
                      <p className="text-2xl font-bold text-purple-400">{sizeInfo.reduction_percent}%</p>
                    </div>
                  </div>
                </div>
                <CheckCircle className="w-16 h-16 text-green-400" />
              </div>
            </motion.div>
          )}

          {/* Info Banner */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-purple-400 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold mb-1">About This Kernel</h3>
                <p className="text-sm text-gray-300">
                  This kernel contains 15 primitive categories specifically optimized for relationship/marriage predictions.
                  It includes D1, D7, D9, D60 chart data, Jaimini techniques, KP cusps, and event-time calculations.
                  <span className="text-purple-400 font-semibold"> Perfect for LLM-based astrological analysis.</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Kernel Sections */}
        <div className="space-y-4">
          {kernel && Object.entries(kernel).map(([key, value], index) => {
            if (key === 'metadata') return null; // Skip metadata in main display
            
            const isExpanded = expandedSections[key];
            
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden"
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(key)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getSectionIcon(key)}</span>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold">{getSectionTitle(key)}</h3>
                      <p className="text-sm text-gray-400">
                        {typeof value === 'object' && !Array.isArray(value) 
                          ? `${Object.keys(value).length} properties`
                          : Array.isArray(value) 
                          ? `${value.length} items`
                          : 'Value'}
                      </p>
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </motion.div>
                </button>

                {/* Section Content */}
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 py-4 border-t border-white/10 bg-black/20"
                  >
                    <pre className="text-sm text-gray-300 overflow-x-auto bg-black/40 p-4 rounded-lg">
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Metadata Footer */}
        {kernel?.metadata && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-6"
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileJson className="w-5 h-5" />
              Kernel Metadata
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Generated At</p>
                <p className="font-semibold">{new Date(kernel.metadata.generated_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-400">Version</p>
                <p className="font-semibold">{kernel.metadata.kernel_version}</p>
              </div>
              <div>
                <p className="text-gray-400">Domain</p>
                <p className="font-semibold text-pink-400">{kernel.metadata.domain}</p>
              </div>
              <div>
                <p className="text-gray-400">Person ID</p>
                <p className="font-semibold">{kernel.metadata.person_id}</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RelationshipKernelViewer;
